import { useMemo } from 'react'
import { useAccount, useReadContracts } from 'wagmi'
import { ADDRESSES, IS_LIVE, ZERO_ADDR } from '@/lib/contracts'
import tokenAbi   from '@/lib/abis/HorizonToken.json'
import farmAbi    from '@/lib/abis/HorizonEmissions.json'
import poolAbi    from '@/lib/abis/HorizonLendingPool.json'
import veAbi from '@/lib/abis/HorizonVoteEscrow.json'
import { ERC20_ABI } from '@/lib/erc20'
import { POLL_SLOW, toNumber } from '@/lib/utils'
import { computeUtilizationBps, computeBorrowRateBps, computeSupplyRateBps, computeAccountUsd, computeAdaptiveBorrowRateBps, adaptiveRateAtTargetBps, ADAPTIVE_TARGET_BPS } from '@/lib/poolMath'
import { useHorizonPriceUsd } from '@/hooks/useHorizonPriceUsd'

const MAX_UINT256 = (1n << 256n) - 1n
const YEAR_SECONDS = 365 * 24 * 60 * 60
/// The old hUSD_DEPLOYED flag is gone, and with it the whole "deployed" concept. It existed to keep
/// a reserve out of the UI when its address was hardcoded here but the pool had never heard of it -
/// a mismatch that cannot occur now, because the list IS the pool's registry. If hUSD is not listed
/// on-chain it simply is not in the array, and nothing downstream renders a "0.00 hUSD" market that
/// is not live.
///
/// HorizonLendingPoolViews.getReservesList - the pool's own registry of listed reserves, written by
/// addReserve. Declared inline rather than imported from a JSON ABI: it is one function, and the
/// views contract is a read surface whose whole job is to be trivially callable.
const VIEWS_ABI = [{
  type: 'function', name: 'getReservesList', stateMutability: 'view',
  inputs: [], outputs: [{ name: '', type: 'address[]' }],
}]

/// **The reserve set comes from the chain, not from this file.** `getReservesList()` is the pool's
/// own registry, written by `addReserve`, so a reserve genesis lists appears here with no code
/// change, no rebuild and no frontend deploy - which is the whole point. The previous hardcoded
/// table meant an asset could be perfectly configured on-chain and still be invisible in the UI,
/// and it made listing a long-tail asset a launch blocker rather than one Safe transaction.
///
/// Everything a reserve entry needs is already on-chain: `reserveConfig` carries `sTokenAddress`,
/// `decimals` and `isFacilitator`, and the underlying ERC20 carries `symbol`. Nothing about a
/// listing has to be duplicated client-side.
///
/// Two things deliberately stay local, because neither is knowable from the chain:
///
/// - **`RESERVE_OVERRIDES`** - display identity. WETH's on-chain symbol is "WETH", but the reserve
///   is ETH from a user's point of view, and every dashboard component matches on `key === 'eth'`
///   to render ETH-flavoured copy and (in MarketsSection) to route through `WETHGateway` rather
///   than the pool. That routing is a real behavioural difference no `symbol()` call implies.
/// - **`DISPLAY_ORDER`** - `getReservesList()` returns registration order, so listing a new asset
///   would otherwise push it to the bottom regardless of importance. Anything unlisted here sorts
///   after the named ones, alphabetically, so a newly listed reserve still lands somewhere stable
///   rather than jumping around between reads.
const RESERVE_OVERRIDES = {
  [(ADDRESSES.WETH || '').toLowerCase()]: { key: 'eth', symbol: 'ETH' },
}
const DISPLAY_ORDER = ['eth', 'wbtc', 'wsteth', 'usdx', 'husd']

/// No first-paint skeleton any more (removed for the staged launch): `d.reserves` is ALWAYS the pool's
/// real registry - `getReservesList()` - and is `[]` when nothing is listed, never a set of fake
/// placeholder reserves. A pool deployed with zero reserves therefore renders as zero (each `addReserve`
/// makes exactly one market appear), and the brief dependent-read gap is a loading state (`isLoading`),
/// not fake rows. Consumers that look a reserve up by key (`find((r) => r.key === 'eth')`) must treat a
/// miss as "not listed" rather than assuming it exists.
/// Slug for a reserve with no override: lowercase, non-alphanumerics stripped, so `wstETH` becomes
/// `wsteth` and stays a valid read-key prefix and object key.
function slugFor(symbol, address) {
  const s = (symbol || '').toLowerCase().replace(/[^a-z0-9]/g, '')
  return s || `r${(address || '').slice(2, 8).toLowerCase()}`
}

/// Aggregated protocol state, read straight off every contract, in one multicall batch:
/// HorizonToken (supply + distribution pockets), HorizonEmissions, HorizonVoteEscrow (locks), and
/// HorizonLendingPool - the single Aave-like symmetric money market that replaced the earlier
/// two-market design (`EarnPool` + `HorizonMoneyMarket` "Market A" + `HorizonMoneyMarketEth`
/// "Market B"). `d.reserves` is an array (currently WETH + USDx, plus hUSD once launched) rather
/// than the old hardcoded `eth*`/`earn*`-prefixed field set - each entry self-describes its own
/// config/state/rates/user-position, so a dashboard component can map over it instead of
/// hand-wiring a field per market. A reserve added later via `pool.addReserve` only needs a new
/// entry in the `reserves` list below, not a new field set threaded through every consumer.
///
/// The pool is WETH-only now, not native-ETH - `HorizonLendingPool` carries no native-ETH handling
/// at all (see its own doc comment); native ETH is handled entirely at the edges by
/// `WETHGateway.sol`. This reserve entry KEEPS the key `'eth'` (not renamed to `'weth'`) - it's
/// still the ETH-denominated reserve from a user's point of view, and every dashboard component
/// keys off `key === 'eth'` to render ETH-flavored copy/icons and (in MarketsSection specifically)
/// to route wallet actions through the gateway instead of the pool directly. `asset` below always
/// points at the real WETH contract address, since that's what every contract read/write actually
/// needs.
///
/// Each multicall entry below carries its own `key` instead of being read back out of `data` by
/// raw array position - inserting or reordering a read doesn't silently shift anything else.
///
/// `overrideAddress` lets a caller read another address's position instead of the connected
/// wallet's own - used only by PortfolioSection's read-only watch-address view (see
/// useWatchedAddresses). Every other page calls this with no argument and keeps reading the
/// real connected wallet, since they need a genuine signer for their own actions.
export function useProtocolState(overrideAddress) {
  const { address: connected } = useAccount()
  const user = overrideAddress ?? connected
  const enabled = IS_LIVE
  const { rate: horizonUsd, found: hasHorizonPrice } = useHorizonPriceUsd()

  // ── Phase 1: which reserves exist ────────────────────────────────────────────────────────────
  // Its own hook rather than part of the batch below, because the batch's CONTENTS depend on the
  // answer - there is no way to ask for per-reserve data before knowing the reserve set. This is
  // the one dependent read the chain-derived list costs, and it is cheap: a single call returning
  // an address array, cached and polled on the same slow interval as everything else.
  const { data: listData, isLoading: listLoading } = useReadContracts({
    query: { enabled, refetchInterval: POLL_SLOW },
    contracts: [{ address: ADDRESSES.POOL_VIEWS, abi: VIEWS_ABI, functionName: 'getReservesList' }],
  })

  // ── Phase 1b: symbols, so a reserve can name itself ──────────────────────────────────────────
  // Split from phase 2 only because `slugFor` needs the symbol to build phase 2's read KEYS, and a
  // key can't be derived from a value that hasn't arrived yet.
  const assets = useMemo(() => (listData?.[0]?.result ?? []).map((a) => a), [listData])
  const { data: metaData } = useReadContracts({
    query: { enabled: enabled && assets.length > 0, refetchInterval: POLL_SLOW },
    contracts: assets.flatMap((a) => [
      { address: a, abi: ERC20_ABI, functionName: 'symbol' },
      // reserveConfig again here, ahead of the main batch, purely for sTokenAddress: userReads
      // needs it to build an address-specific call, and a call's ADDRESS cannot come from the same
      // batch it lives in. Everything else still reads config from the batch below, so there is one
      // source of truth for the config tuple - this only borrows one field early.
      { address: ADDRESSES.LENDING_POOL, abi: poolAbi, functionName: 'getReserveConfig', args: [a] },
    ]),
  })

  /// The reserve set, assembled from the chain and ordered for display. `sToken`/`decimals`/
  /// `isFacilitator` are NOT here - they come from `reserveConfig` in the batch below, so they are
  /// never duplicated or allowed to disagree with it.
  const RESERVES = useMemo(() => {
    const list = assets.map((asset, i) => {
      const chainSymbol = metaData?.[i * 2]?.result
      const cfg = metaData?.[i * 2 + 1]?.result
      const o = RESERVE_OVERRIDES[(asset || '').toLowerCase()] ?? {}
      return {
        asset,
        symbol: o.symbol ?? chainSymbol ?? '—',
        key: o.key ?? slugFor(chainSymbol, asset),
        sToken: cfg ? cfg.sTokenAddress : null,
      }
    })
    // Named reserves in the order given; anything newly listed sorts after them, alphabetically,
    // so it lands somewhere stable instead of moving between reads.
    // `!metaData` matters as much as an empty list, and is the subtler of the two: phase 1 resolves
    // BEFORE phase 1b, so there is a window where the addresses are known but the symbols are not.
    // Deriving keys from missing symbols yields address slugs, every `find((r) => r.key === 'usdx')`
    // misses, and the app crashes on a list that is about to be correct. Hold the skeleton until
    // the names are actually in hand.
    // Empty is honest: a pool with zero listed reserves is [], not a fake set. Also hold [] while the
    // addresses are known but their symbols are not - deriving keys from missing symbols yields
    // address slugs that every `find(r => r.key === 'usdx')` misses. `isLoading` covers this window.
    if (list.length === 0 || !metaData) return []
    return list.sort((a, b) => {
      const ia = DISPLAY_ORDER.indexOf(a.key), ib = DISPLAY_ORDER.indexOf(b.key)
      if (ia !== -1 || ib !== -1) return (ia === -1 ? 1e9 : ia) - (ib === -1 ? 1e9 : ib)
      return a.key.localeCompare(b.key)
    })
  }, [assets, metaData])

  // ── Phase 2: everything else, in one batch ───────────────────────────────────────────────────
  const reads = useMemo(() => [...globalReads(RESERVES), ...userReads(user, RESERVES)], [RESERVES, user])

  const { data, refetch, isLoading: batchLoading } = useReadContracts({
    query: { enabled: enabled && RESERVES.length > 0, refetchInterval: POLL_SLOW },
    contracts: reads.map(({ key, ...call }) => call),
  })
  // Loading spans BOTH dependent reads: the reserve list, and (for a non-empty list) its symbols -
  // reserves is held at [] through the symbol window, so without this a loaded-but-still-resolving
  // pool would look like "zero markets" for a frame. A genuinely empty pool has no symbol read, so
  // this stays false and the empty state shows immediately.
  const isLoading = listLoading || (assets.length > 0 && !metaData) || batchLoading

  // Always return a fully-shaped `d`, deployed or not, loaded or not - the app renders its real
  // layout from the first paint, filled with zeroes until there's something live to show.
  if (!enabled || !data) {
    return {
      isLoading, deployed: enabled, refetch,
      d: {
        horizonTotalSupply: 0, horizonInEmissions: 0,
        emissionsPerSecond: 0n,

        totalLocked: 0,
        totalBurned: 0,
        userStaked: 0, userStakedWei: 0n, userStakingPendingHorizon: 0, userStakingPendingHorizonWei: 0n,
        userHorizonBalWei: 0n,

        closeFactorBps: 0,
        treasury: null,
        accountTotalCollateralUsd: 0, accountTotalDebtUsd: 0, accountAvailableBorrowsUsd: 0,
        healthFactor: null, hasDebt: false,

        // Empty until phase 1 answers; then correctly shaped per real reserve. isFacilitator is
        // false here because it lives in reserveConfig, which has not arrived yet - it only
        // affects rate display, never a number that could mislead at zero.
        reserves: RESERVES.map((r) => emptyReserve(r.key, r.symbol, r.asset, false)),
      },
    }
  }

  const raw = {}
  reads.forEach((read, i) => { raw[read.key] = data[i]?.result })

  const healthFactorRaw = raw.healthFactor
  const hasDebt = healthFactorRaw != null && healthFactorRaw !== MAX_UINT256

  const d = {
    horizonTotalSupply: toNumber(raw.horizonTotalSupply),
    horizonInEmissions: toNumber(raw.horizonInEmissions),

    emissionsPerSecond: raw.emissionsPerSecond ?? 0n,

    // Committed-HORIZON TVL = the decaying-lock principal the ve holds (the veNFT rewrite dropped the
    // old totalLocked getter; the held balance is the same figure and cannot drift from it).
    totalLocked:            toNumber(raw.veLockedHorizon),
    // Burned HORIZON, now a direct getter (no more halving the burn power).
    totalBurned:            toNumber(raw.veTotalBurnedHorizon),
    // Per-user locked principal has no single getter in the veNFT model (a wallet holds many tokens);
    // components that need it sum a holder's positions via `useVeNFTs`.
    userHorizonBalWei:             user ? (raw.userHorizonBalWei ?? 0n) : 0n,

    closeFactorBps: Number(raw.closeFactorBps ?? 0n),
    // Revenue destination every fee stream in this protocol pays into - genesis-settable, so
    // read live rather than hardcoded (StatsSection's own Treasury panel, and the CoW Swap
    // widget's partner-fee recipient, both key off this).
    treasury: raw.treasury ?? null,
    // Raw SCALE-based (1e18) health factor, or null before the first read - `type(uint256).max`
    // means "no debt anywhere, never liquidatable" and is intentionally NOT collapsed into a
    // finite number here (dividing it down would just produce a meaningless huge float); check
    // `hasDebt` first. Below 1e18 means eligible for liquidationCall.
    healthFactor: user ? (healthFactorRaw ?? null) : null,
    hasDebt: user ? hasDebt : false,
  }

  // No display filter any more: every entry came from the pool's own registry, so every entry is
  // by definition live. The previous split between "built" and "displayed" existed only to hide a
  // hardcoded address the pool had never heard of, which is now impossible by construction.
  d.reserves = RESERVES.map((r) => {
    // isFacilitator comes from reserveConfig rather than a local flag - the contract is the only
    // thing that actually knows, and a facilitator never holds real liquidity (it mints on borrow
    // rather than lending out deposits), so its utilization reads ~100% by design. Harmless: hUSD's
    // borrow rate is flat, so utilization never affects the rate shown.
    const isFacilitator = Boolean(raw[`${r.key}Config`]?.isFacilitator)
    return buildReserve({
      key: r.key, symbol: r.symbol, asset: r.asset, sTokenAddress: r.sToken,
      raw, user, horizonUsd, hasHorizonPrice, isFacilitator,
      liquidBalanceRaw: isFacilitator ? 0n : (raw[`${r.key}BalanceOfPool`] ?? 0n),
    })
  })
  const built = d.reserves

  // HorizonLendingPool's own (removed) `accountData(user)` equivalent - see computeAccountUsd's own
  // doc comment in src/lib/poolMath.js for what changed moving this client-side.
  const { totalCollateralUsd, totalDebtUsd, availableBorrowsUsd } = user
    ? computeAccountUsd(built.map((r) => ({
        supplyBalance: r.user.supplyBalance, debt: r.user.debt,
        priceUsd: r.priceUsd, collateralFactorBps: r.collateralFactorBps,
      })))
    : { totalCollateralUsd: 0, totalDebtUsd: 0, availableBorrowsUsd: 0 }
  d.accountTotalCollateralUsd = totalCollateralUsd
  d.accountTotalDebtUsd = totalDebtUsd
  d.accountAvailableBorrowsUsd = availableBorrowsUsd
  return { isLoading, deployed: true, refetch, d }
}

function cap(s) { return s.charAt(0).toUpperCase() + s.slice(1) }

function emptyReserve(key, symbol, asset, isFacilitator = false) {
  return {
    key, symbol, asset, sTokenAddress: null, isFacilitator,
    // 18 is the pre-read placeholder, not an assumption about the asset - the real value arrives
    // with the first config read. Forms are disabled until `deployed` anyway.
    decimals: 18,
    collateralFactorBps: 0, liquidationThresholdBps: 0, liquidationBonusBps: 0,
    reserveFactorBps: 0, liquidationFeeBps: 0,
    baseRateBps: 0, slope1Bps: 0, slope2Bps: 0, kinkBps: 0,
    useAdaptiveIrm: false, rateAtTargetPerSec: 0n, rateAtTargetBps: 0, adaptiveTargetBps: 9_000,
    supplyCap: 0, supplyCapWei: 0n, borrowCap: 0, borrowCapWei: 0n,
    supplyIndex: 0n, borrowIndex: 0n,
    totalBorrows: 0, totalBorrowsWei: 0n, totalReservesAccrued: 0, totalReservesAccruedWei: 0n,
    totalSupplied: 0, totalSuppliedWei: 0n,
    utilizationBps: 0, borrowRateBps: 0, supplyRateBps: 0, priceUsd: 0,
    priceFeed: null, priceStaleAfterSec: 0, maxPriceAgeSec: 0, liquidityPremiumBps: 0,
    isActive: false, isPaused: false,
    availableLiquidity: 0, availableLiquidityWei: 0n,
    supplyHorizonPerSecond: 0n, borrowHorizonPerSecond: 0n,
    supplyHorizonBudgetPerSecond: 0n, borrowHorizonBudgetPerSecond: 0n,
    supplyAprCapBps: 0, borrowAprCapBps: 0,
    supplyHorizonAprBps: 0, borrowHorizonAprBps: 0,
    totalSupplyAprBps: 0, totalBorrowAprBps: 0,
    user: {
      supplyBalance: 0, supplyBalanceWei: 0n, sTokenBalanceWei: 0n,
      debt: 0, debtWei: 0n, pendingSupplyHorizon: 0, pendingBorrowHorizon: 0,
    },
  }
}

/// Tuple index maps, derived from the ABI at module load rather than hardcoded.
///
/// viem returns these multi-output getters as a plain ARRAY - named access (`config.supplyCap`)
/// is undefined - so every field has to be read positionally, and a hardcoded position silently
/// becomes wrong the moment anyone inserts a field into the struct. That is not hypothetical: every
/// read below was off by one, so the app displayed `liquidationThresholdBps` as the collateral
/// factor, `liquidationBonusBps` as the threshold, and the raw `priceFeed` ADDRESS as the
/// liquidation bonus (rendering as 7.9e47). Deriving the map from the ABI makes the position follow
/// the struct automatically, so adding a field can never shift what the frontend reads again.
const idxMap = (fnName) => Object.fromEntries(
  (poolAbi.find((f) => f.type === 'function' && f.name === fnName)?.outputs ?? []).map((o, i) => [o.name, i]),
)
/// `reserveConfig`'s index map is GONE, and so is the flat tuple it indexed. The pool's config
/// mapping is `internal` now and `getReserveConfig` returns a NAMED struct, which viem decodes to an
/// object - so this file reads `config.decimals` directly. That is strictly better than an
/// ABI-derived index: there is no position to derive, and the same change removed the hazard on the
/// Solidity side too, where it was genuinely dangerous (see IHorizonLendingPool's own comment).
///
/// `reserveState` still returns a flat tuple, so it still needs its map.
const ST = idxMap('reserveState')
// The on-chain "uncapped" sentinel after entry 164's fail-closed flip.
const UNCAPPED_MAX = 2n ** 128n - 1n

/// Builds one `d.reserves[]` entry from the flat `raw` multicall results, keyed by the same
/// `${key}...`/`user${Cap(key)}...` naming `globalReads`/`userReads` declare below - `key` is
/// 'eth' or 'usdx' (lowercase, matches the read-key prefix), `Cap(key)` is 'Eth'/'Usdx' (matches
/// the user-read prefix, since JS identifiers can't start lowercase-then-mid-capitalize cleanly
/// off a variable).
function buildReserve({ key, symbol, asset, sTokenAddress, raw, user, horizonUsd, hasHorizonPrice, liquidBalanceRaw, isFacilitator = false }) {
  const Key = cap(key)
  const config = raw[`${key}Config`] // ReserveConfig tuple, see HorizonLendingPool.sol
  const state  = raw[`${key}State`]  // ReserveState tuple
  // viem decodes every Solidity uint*/int* type (including uint8) as bigint - Number() here so
  // `decimals === 18`/`18 - decimals` (both computeUtilizationBps and buildUserPosition do this)
  // are plain JS number ops, not a bigint/number mix (which throws).
  const decimals = config ? Number(config.decimals) : 18

  // utilizationBps/borrowRateBps/supplyRateBps used to be read straight off the pool
  // (utilizationBps/borrowRateBps/supplyRateBps); all three were removed from HorizonLendingPool
  // (zero internal callers, pure frontend convenience) and are computed here instead - see
  // src/lib/poolMath.js's own doc comments for what each mirrors.
  const utilBpsBig = config && state
    ? computeUtilizationBps(state[ST.totalBorrows], liquidBalanceRaw ?? 0n, decimals)
    : 0n
  // Adaptive IRM (entry 239) is per-reserve, toggled on-chain via `useAdaptiveIrm` on ReserveState.
  // When it is on, the reserve charges the Morpho adaptive curve off its stored `rateAtTarget`, NOT
  // the kinked base/slope/kink params (which stay as the instant fallback). Compute whichever the
  // reserve is actually on, so every rate this app shows matches what the chain charges.
  const useAdaptiveIrm = state ? !!state[ST.useAdaptiveIrm] : false
  const rateAtTargetPerSec = state ? state[ST.rateAtTarget] : 0n
  const borrowRateBpsBig = !config
    ? 0n
    : useAdaptiveIrm
      ? computeAdaptiveBorrowRateBps(utilBpsBig, rateAtTargetPerSec)
      : computeBorrowRateBps(utilBpsBig, config.baseRateBps, config.slope1Bps, config.slope2Bps, config.kinkBps)
  const supplyRateBpsBig = config
    ? computeSupplyRateBps(utilBpsBig, borrowRateBpsBig, config.reserveFactorBps)
    : 0n

  const totalBorrowsNum  = state ? toNumber(state[ST.totalBorrows]) : 0
  const totalSuppliedNum = state ? toNumber((state[ST.totalHTokenShares] * state[ST.supplyIndex]) / 1_000_000_000_000_000_000n) : 0
  const priceUsdNum      = toNumber(raw[`${key}PriceUsd`] ?? 0n)
  const supplyRateBpsNum = Number(supplyRateBpsBig)
  const borrowRateBpsNum = Number(borrowRateBpsBig)

  // HORIZON emission rate per second, supply and borrow, read straight off the controller's
  // `effectiveRatePerSecond` - the ONE per-farm number after the APR governor (entry 180) and the
  // farm's end date, so what this page shows is by construction what accrual actually pays. The
  // old `emissionsPerSecond x weightBps / BPS` arithmetic lives on-chain now; recomputing it here
  // would silently show the ungoverned rate the moment a cap binds.
  const supplyHorizonPerSecond = raw[`${key}SupplyRate`] ?? 0n
  const borrowHorizonPerSecond = raw[`${key}BorrowRate`] ?? 0n
  // Whether the APR governor is currently the binding limit (effective < budgeted) - drives the
  // small "capped" hint on the Markets sub-lines, so a governed rate reads as a policy, not a bug.
  const eps = raw.emissionsPerSecond ?? 0n
  const supplyHorizonCapped = supplyHorizonPerSecond < (eps * (raw[`${key}SupplyWeightBps`] ?? 0n)) / 10_000n
  const borrowHorizonCapped = borrowHorizonPerSecond < (eps * (raw[`${key}BorrowWeightBps`] ?? 0n)) / 10_000n

  // Annualized HORIZON-reward rate, in the same bps convention supplyRateBps/borrowRateBps already
  // use, so a consumer can add (supply) or subtract (borrow) it directly. Needs a real HORIZON price
  // and non-zero reserve size to mean anything - 0 otherwise, same "no guess" convention every
  // other USD conversion in this app already follows.
  const supplyHorizonAprBps = hasHorizonPrice && totalSuppliedNum > 0 && priceUsdNum > 0
    ? (toNumber(supplyHorizonPerSecond) * YEAR_SECONDS * horizonUsd) / (totalSuppliedNum * priceUsdNum) * 10_000
    : 0
  const borrowHorizonAprBps = hasHorizonPrice && totalBorrowsNum > 0 && priceUsdNum > 0
    ? (toNumber(borrowHorizonPerSecond) * YEAR_SECONDS * horizonUsd) / (totalBorrowsNum * priceUsdNum) * 10_000
    : 0

  return {
    key, symbol, asset, sTokenAddress, isFacilitator,
    collateralFactorBps:     config ? Number(config.collateralFactorBps) : 0,
    liquidationThresholdBps: config ? Number(config.liquidationThresholdBps) : 0,
    liquidationBonusBps:     config ? Number(config.liquidationBonusBps) : 0,
    baseRateBps:             config ? Number(config.baseRateBps) : 0,
    slope1Bps:                config ? Number(config.slope1Bps) : 0,
    slope2Bps:                config ? Number(config.slope2Bps) : 0,
    kinkBps:                  config ? Number(config.kinkBps) : 0,
    // Adaptive IRM (entry 239). `useAdaptiveIrm` picks which model is live; when adaptive, the kink/
    // slope fields above are the dormant fallback and `rateAtTarget*` describe the active curve.
    useAdaptiveIrm,
    rateAtTargetPerSec,                                                      // per-second WAD int, raw
    rateAtTargetBps: useAdaptiveIrm ? Number(adaptiveRateAtTargetBps(rateAtTargetPerSec)) : 0,
    adaptiveTargetBps: ADAPTIVE_TARGET_BPS,                                  // 90% pivot
    reserveFactorBps:        config ? Number(config.reserveFactorBps) : 0,
    liquidationFeeBps:       config ? Number(config.liquidationFeeBps) : 0,
    // 0 means uncapped (HorizonLendingPool's own convention - addReserve never sets a cap on its
    // own, see setReserveCaps). Stored in the reserve's OWN native decimals, not canonical
    // 18-decimal - toNumber needs this reserve's own `decimals`, not the default.
    // Exposed, not just used locally: every form that turns a typed amount into wei needs the
    // reserve's OWN decimals. They all hardcoded 18, which was invisible while every reserve was
    // 18-decimal and becomes a 10-orders-of-magnitude error the moment one isn't (WBTC is 8).
    decimals,

    // Cap semantics flipped on-chain (entry 164): 0 = CLOSED, type(uint128).max = uncapped. The
    // UI's historical convention is "0 = no cap to display", so the explicit uncapped max maps
    // back to 0 HERE, at the boundary, and every consumer (CapIndicator etc.) stays unchanged. A
    // genuinely CLOSED reserve (raw 0) also displays no cap bar - production listings always set
    // caps in the same batch, so that state exists only mid-listing.
    supplyCapWei: config ? config.supplyCap : 0n,
    supplyCap:    config && config.supplyCap !== UNCAPPED_MAX ? toNumber(config.supplyCap, decimals) : 0,
    borrowCapWei: config ? config.borrowCap : 0n,
    borrowCap:    config && config.borrowCap !== UNCAPPED_MAX ? toNumber(config.borrowCap, decimals) : 0,

    // Isolation mode (see contracts/libraries/IsolationLogic.sol). A NON-ZERO ceiling is what
    // makes a reserve isolated - there is no separate flag, so these two can never disagree.
    // `isolationDebtUsd` is the running total already borrowed against it, across all users.
    isolationCeilingUsd: config ? toNumber(config.isolationDebtCeilingUsd) : 0,
    isIsolated:          config ? config.isolationDebtCeilingUsd > 0n : false,
    borrowableInIsolation: config ? Boolean(config.borrowableInIsolation) : false,
    // Collateral-only listing (ALM vault shares are the first). Read so the UI can leave the Borrow
    // action off the row entirely rather than offering a transaction that reverts
    // `BorrowingDisabled` after the user has signed - the same failure /liquidations shipped with
    // and CHANGELOG.md entry 109 fixed.
    borrowingDisabled: config ? Boolean(config.borrowingDisabled) : false,
    isolationDebtUsd:    toNumber(raw[`${key}IsolationDebt`] ?? 0n),

    supplyIndex: state ? state[ST.supplyIndex] : 0n,
    borrowIndex: state ? state[ST.borrowIndex] : 0n,
    totalBorrowsWei:         state ? state[ST.totalBorrows] : 0n,
    totalBorrows:             totalBorrowsNum,
    totalReservesAccruedWei: state ? state[ST.totalReservesAccrued] : 0n,
    totalReservesAccrued:    state ? toNumber(state[ST.totalReservesAccrued]) : 0,
    // Underlying-denominated total supplied - not a direct on-chain field, derived the same way
    // HToken's own exchange rate does: shares * supplyIndex / SCALE.
    totalSuppliedWei: state ? (state[ST.totalHTokenShares] * state[ST.supplyIndex]) / 1_000_000_000_000_000_000n : 0n,
    totalSupplied:    totalSuppliedNum,

    utilizationBps: Number(utilBpsBig),
    borrowRateBps:  borrowRateBpsNum,
    supplyRateBps:  supplyRateBpsNum,
    priceUsd:       priceUsdNum,

    // Detail-page fields. Every one of these was ALREADY fetched - they are members of the same
    // `reserveConfig` tuple and the same pool-balance read the lines above consume - so surfacing
    // them costs no additional RPC, only the decode. Added when /markets/:asset needed to show the
    // oracle a price comes from and how stale it may be before the pool rejects it.
    priceFeed:          config ? config.priceFeed : null,
    priceStaleAfterSec: config ? Number(config.priceStaleAfter) : 0,
    maxPriceAgeSec:     config ? Number(config.maxPriceAge) : 0,
    liquidityPremiumBps: config ? Number(config.liquidityPremiumBps) : 0,
    isActive:  config ? Boolean(config.isActive) : false,
    isPaused:  config ? Boolean(config.paused) : false,
    // The pool's own real balance of the underlying - what a borrower can actually draw right now,
    // and the ceiling on any loop regardless of its leverage headroom. Already read for the
    // utilisation denominator above.
    availableLiquidityWei: liquidBalanceRaw ?? 0n,
    availableLiquidity:    toNumber(liquidBalanceRaw ?? 0n, decimals),

    // Per-second HORIZON emission rate this reserve currently pays, and the annualized rate/APY
    // figures derived from it. supplyRateBps/borrowRateBps above stay pure-interest (unchanged,
    // still useful on their own); totalSupplyAprBps/totalBorrowAprBps are what a "Supply APY"/
    // "Borrow APY" headline number should actually show - HORIZON rewards ADD to what a supplier
    // earns, and OFFSET (subtract from) what a borrower pays, matching the real economics (and
    // the same convention Aave's own "Net APY" uses). totalBorrowAprBps can go negative when
    // HORIZON rewards outweigh the interest cost - a real, valid state (you're being paid to
    // borrow), not clamped away.
    supplyHorizonPerSecond, borrowHorizonPerSecond,
    supplyHorizonCapped, borrowHorizonCapped,
    // Ungoverned budget + governor ceiling, the two inputs a cold-start estimate needs (the
    // effective rates above read zero on an empty farm by the governor's own min()).
    supplyHorizonBudgetPerSecond: (eps * (raw[`${key}SupplyWeightBps`] ?? 0n)) / 10_000n,
    borrowHorizonBudgetPerSecond: (eps * (raw[`${key}BorrowWeightBps`] ?? 0n)) / 10_000n,
    supplyAprCapBps: Number(raw[`${key}SupplyCapBps`] ?? 0n),
    borrowAprCapBps: Number(raw[`${key}BorrowCapBps`] ?? 0n),
    supplyHorizonAprBps, borrowHorizonAprBps,
    totalSupplyAprBps: supplyRateBpsNum + supplyHorizonAprBps,
    totalBorrowAprBps: borrowRateBpsNum - borrowHorizonAprBps,

    user: buildUserPosition({ Key, raw, user, decimals }),
  }
}

/// Per-user pending HORIZON now comes wholly from the single emissions controller (entry 177's
/// dormant-code removal deleted the pool's own `reserveIncentives`/`userReserves` incentive fields):
/// supply is read by the reserve's sToken farm key, borrow by the reserve asset key, both via the
/// controller's `pendingVaultHorizonOf`.
function buildUserPosition({ Key, raw, user, decimals }) {
  const supplyBalanceWei = user ? (raw[`user${Key}SupplyBalance`] ?? 0n) : 0n
  const debtWei          = user ? (raw[`user${Key}Debt`] ?? 0n) : 0n
  const sTokenBalanceWei = user ? (raw[`user${Key}HTokenWei`] ?? 0n) : 0n

  // Unified controller pending (entry 177) - the only source now.
  const ctrlSupplyPendingWei = user ? (raw[`user${Key}CtrlSupplyPending`] ?? 0n) : 0n
  const ctrlBorrowPendingWei = user ? (raw[`user${Key}CtrlBorrowPending`] ?? 0n) : 0n

  // `decimals` is REQUIRED on these two. `supplyBalanceOf`/`debtOf` return the asset's OWN native
  // decimals (both end in PoolMath.fromCanonical), unlike `reserveState.totalBorrows` above, which
  // is canonical 18. Defaulting to 18 here was harmless only for as long as every reserve happened
  // to BE 18 decimals - WBTC is 8, so an 0.5 WBTC position would have displayed as 0.000000005.
  return {
    supplyBalance: user ? toNumber(supplyBalanceWei, decimals) : 0,
    supplyBalanceWei,
    sTokenBalanceWei,
    debt: user ? toNumber(debtWei, decimals) : 0,
    debtWei,
    pendingSupplyHorizon: toNumber(ctrlSupplyPendingWei),
    pendingBorrowHorizon: toNumber(ctrlBorrowPendingWei),
  }
}

function globalReads(RESERVES) {
  const pool = ADDRESSES.LENDING_POOL
  return [
    { key: 'horizonTotalSupply', address: ADDRESSES.HORIZON_TOKEN, abi: tokenAbi, functionName: 'totalSupply' },
    { key: 'horizonInEmissions', address: ADDRESSES.HORIZON_TOKEN, abi: tokenAbi, functionName: 'balanceOf', args: [ADDRESSES.HORIZON_EMISSIONS] },

    { key: 'emissionsPerSecond', address: ADDRESSES.HORIZON_EMISSIONS, abi: farmAbi, functionName: 'emissionsPerSecond' },

    // HorizonVoteEscrow - global (entry 182: the lock is the stake). The veNFT rewrite removed the old
    // `totalLocked`/`totalBurnedPower` getters. Total DECAYING-lock principal is just the HORIZON the ve
    // holds (burned HORIZON is destroyed, not held), and burned principal now has a direct getter.
    { key: 'veLockedHorizon', address: ADDRESSES.HORIZON_TOKEN, abi: tokenAbi, functionName: 'balanceOf', args: [ADDRESSES.HORIZON_VOTE_ESCROW] },
    { key: 'veTotalBurnedHorizon', address: ADDRESSES.HORIZON_VOTE_ESCROW, abi: veAbi, functionName: 'totalBurnedHorizon' },

    { key: 'closeFactorBps', address: pool, abi: poolAbi, functionName: 'closeFactorBps' },
    { key: 'treasury',       address: pool, abi: poolAbi, functionName: 'treasury' },

    // Five reads per reserve, generated from RESERVES - see that table's own doc comment. The ETH
    // reserve is WETH-backed and otherwise entirely ordinary: the pool has no native-ETH concept
    // anymore, so there's no separate native-balance read the way a pre-WETHGateway version needed.
    ...RESERVES.flatMap((r) => [
      { key: `${r.key}IsolationDebt`, address: pool, abi: poolAbi, functionName: 'isolationDebtUsd', args: [r.asset] },
      { key: `${r.key}Config`,        address: pool, abi: poolAbi, functionName: 'getReserveConfig',    args: [r.asset] },
      { key: `${r.key}State`,         address: pool, abi: poolAbi, functionName: 'reserveState',     args: [r.asset] },
      // Unified emissions controller (entry 177): each reserve's effective supply/borrow HORIZON
      // rate, wei/second, after the APR governor (entry 180) - the controller's own number, so the
      // display can never disagree with what accrual pays. Supply is keyed by the sToken, borrow by
      // the reserve asset - the two farm keys the controller uses.
      { key: `${r.key}SupplyRate`, address: ADDRESSES.HORIZON_EMISSIONS, abi: farmAbi, functionName: 'effectiveRatePerSecond', args: [r.sToken ?? ZERO_ADDR] },
      { key: `${r.key}BorrowRate`, address: ADDRESSES.HORIZON_EMISSIONS, abi: farmAbi, functionName: 'effectiveRatePerSecond', args: [r.asset] },
      // The ungoverned budget's inputs, purely to detect "the cap is binding" for the UI hint.
      { key: `${r.key}SupplyWeightBps`, address: ADDRESSES.HORIZON_EMISSIONS, abi: farmAbi, functionName: 'weightBpsOf', args: [r.sToken ?? ZERO_ADDR] },
      { key: `${r.key}BorrowWeightBps`, address: ADDRESSES.HORIZON_EMISSIONS, abi: farmAbi, functionName: 'weightBpsOf', args: [r.asset] },
      // The farm's APR-governor ceiling (entry 180), read for the cold-start estimate: an empty
      // farm's EFFECTIVE rate is capBps x 0 TVL = zero, so estimating what a first deposit would
      // earn requires the ungoverned budget above bounded by this cap - the same min() accrual
      // itself would apply once the deposit exists.
      { key: `${r.key}SupplyCapBps`, address: ADDRESSES.HORIZON_EMISSIONS, abi: farmAbi, functionName: 'aprCapBpsOf', args: [r.sToken ?? ZERO_ADDR] },
      { key: `${r.key}BorrowCapBps`, address: ADDRESSES.HORIZON_EMISSIONS, abi: farmAbi, functionName: 'aprCapBpsOf', args: [r.asset] },
      { key: `${r.key}PriceUsd`,      address: pool, abi: poolAbi, functionName: 'priceOf',          args: [r.asset] },
      // utilizationBps's "cash" half - the pool's own real liquid balance of the underlying. Read
      // for every reserve including a facilitator: addReserve deploys an sToken for all of them, so
      // the facilitator's reads just return zero rather than needing a branch here. One less place
      // for the read set and the reserve set to disagree.
      { key: `${r.key}BalanceOfPool`, address: r.asset, abi: ERC20_ABI, functionName: 'balanceOf', args: [pool] },
    ]),
  ]
}

// User-scoped (money market + staking + the one HORIZON balance read every page needs - HorizonFarm's
// own per-user reads are farm-count-dependent, so they stay inline in PortfolioSection/
// FarmSection instead of living here). Empty when no wallet is connected.
function userReads(user, RESERVES) {
  if (!user) return []
  const pool = ADDRESSES.LENDING_POOL
  return [
    { key: 'userHorizonBalWei',            address: ADDRESSES.HORIZON_TOKEN,   abi: tokenAbi,   functionName: 'balanceOf', args: [user] },

    { key: 'healthFactor',  address: pool, abi: poolAbi, functionName: 'healthFactor', args: [user] },

    // Per-reserve user position, generated from the same RESERVES table globalReads uses, so a
    // listed asset can never end up with global reads but no user reads (which rendered a market
    // that showed protocol totals and a permanently empty position).
    ...RESERVES.flatMap((r) => [
      { key: `user${cap(r.key)}Debt`,     address: pool, abi: poolAbi, functionName: 'debtOf', args: [r.asset, user] },
      { key: `user${cap(r.key)}SupplyBalance`, address: pool, abi: poolAbi, functionName: 'supplyBalanceOf', args: [r.asset, user] },
      // sToken SHARES, not the underlying balance above - kept for consumers that need the raw
      // share balance (e.g. live-accrual displays), not redundant with supplyBalanceOf.
      ...(r.sToken && r.sToken !== ZERO_ADDR
        ? [{ key: `user${cap(r.key)}HTokenWei`, address: r.sToken, abi: ERC20_ABI, functionName: 'balanceOf', args: [user] }]
        : []),
      // Unified controller pending (entry 177): supply keyed by sToken, borrow by reserve. When the
      // pool is cut over, its own userReserves pending reads 0 and these carry the real figure.
      ...(r.sToken && r.sToken !== ZERO_ADDR
        ? [{ key: `user${cap(r.key)}CtrlSupplyPending`, address: ADDRESSES.HORIZON_EMISSIONS, abi: farmAbi, functionName: 'pendingVaultHorizonOf', args: [r.sToken, user] }]
        : []),
      { key: `user${cap(r.key)}CtrlBorrowPending`, address: ADDRESSES.HORIZON_EMISSIONS, abi: farmAbi, functionName: 'pendingVaultHorizonOf', args: [r.asset, user] },
    ]),
  ]
}
